import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  TAG_ASSIGNMENTS_SEED,
  TAG_CATEGORIES,
  TAGS_SEED,
  TAG_TYPES,
} from '../data';
import type {
  CreateTagInput,
  Tag,
  TagAssignment,
  TagCategory,
  TagPage,
  TagQuery,
  TagType,
  TagTypeKey,
  UpdateTagInput,
} from '../types';

import { CurrentUserService } from './current-user.service';

const LIST_LATENCY_MS = 400;

type TagSortValue = string | number | null | undefined;

@Injectable({ providedIn: 'root' })
export class TagService {
  private readonly currentUser = inject(CurrentUserService);

  get currentUserId(): string {
    return this.currentUser.id();
  }
  readonly types = TAG_TYPES;
  readonly categories = TAG_CATEGORIES;

  private readonly _tags = signal<ReadonlyArray<Tag>>(TAGS_SEED);
  readonly tags = this._tags.asReadonly();

  private readonly _assignments = signal<ReadonlyArray<TagAssignment>>(TAG_ASSIGNMENTS_SEED);
  readonly assignments = this._assignments.asReadonly();

  private readonly _revision = signal<number>(0);
  readonly revision = this._revision.asReadonly();

  private readonly categoryIndex = new Map<string, TagCategory>(
    TAG_CATEGORIES.map((category) => [category.id, category]),
  );
  private readonly typeIndex = new Map<TagTypeKey, TagType>(
    TAG_TYPES.map((type) => [type.key, type]),
  );

  readonly visibleTags = computed<ReadonlyArray<Tag>>(() =>
    this._tags().filter((tag) => this.isVisible(tag)),
  );

  category(id: string): TagCategory | null {
    return this.categoryIndex.get(id) ?? null;
  }

  type(key: TagTypeKey): TagType | null {
    return this.typeIndex.get(key) ?? null;
  }

  typeOfTag(tag: Tag): TagType | null {
    const category = this.category(tag.categoryId);
    return category ? this.type(category.typeKey) : null;
  }

  tagById(id: string): Tag | null {
    return this._tags().find((tag) => tag.id === id) ?? null;
  }

  categoriesForType(typeKey: TagTypeKey): ReadonlyArray<TagCategory> {
    return this.categories.filter((category) => category.typeKey === typeKey);
  }

  isVisible(tag: Tag): boolean {
    if (tag.visibility !== 'private') return true;
    return tag.ownerId === this.currentUserId;
  }

  isEditable(tag: Tag): boolean {
    if (tag.visibility === 'system' || tag.visibility === 'internal') return false;
    if (tag.visibility === 'organisation') return true;
    return tag.ownerId === this.currentUserId;
  }

  objectIdsForTag(tagId: string, objectType: TagTypeKey = 'account'): ReadonlyArray<string> {
    return this._assignments()
      .filter((a) => a.tagId === tagId && a.objectType === objectType)
      .map((a) => a.objectId);
  }

  assignmentCount(tagId: string, objectType: TagTypeKey = 'account'): number {
    return this._assignments().filter((a) => a.tagId === tagId && a.objectType === objectType)
      .length;
  }

  tagIdsForObject(objectId: string, objectType: TagTypeKey = 'account'): ReadonlyArray<string> {
    return this._assignments()
      .filter((a) => a.objectId === objectId && a.objectType === objectType)
      .map((a) => a.tagId);
  }

  tagsForObject(objectId: string, objectType: TagTypeKey = 'account'): ReadonlyArray<Tag> {
    const assigned = new Set(this.tagIdsForObject(objectId, objectType));
    return this._tags().filter((tag) => assigned.has(tag.id) && this.isVisible(tag));
  }

  tagCountForObject(objectId: string, objectType: TagTypeKey = 'account'): number {
    return this.tagsForObject(objectId, objectType).length;
  }

  assignableTagsForType(objectType: TagTypeKey = 'account'): ReadonlyArray<Tag> {
    return this.visibleTags().filter(
      (tag) => this.category(tag.categoryId)?.typeKey === objectType,
    );
  }

  setTagsForObject(
    objectId: string,
    objectType: TagTypeKey,
    tagIds: ReadonlyArray<string>,
  ): void {
    const assignable = new Set(this.assignableTagsForType(objectType).map((tag) => tag.id));
    const next: TagAssignment[] = tagIds
      .filter((tagId) => assignable.has(tagId))
      .map((tagId) => ({ tagId, objectType, objectId }));
    this._assignments.update((all) => [
      ...all.filter(
        (a) => !(a.objectId === objectId && a.objectType === objectType && assignable.has(a.tagId)),
      ),
      ...next,
    ]);
    this.bump();
  }

  createTag(input: CreateTagInput): Tag {
    const objectType = this.objectTypeForCategory(input.categoryId);
    const tag: Tag = {
      id: this.nextId('tag'),
      categoryId: input.categoryId,
      name: input.name.trim(),
      description: input.description.trim(),
      color: input.color,
      visibility: input.visibility,
      ownerId: this.currentUserId,
      createdAt: this.todayIso(),
    };
    this._tags.update((tags) => [...tags, tag]);
    this.replaceAssignments(tag.id, objectType, input.objectIds);
    this.bump();
    return tag;
  }

  updateTag(id: string, input: UpdateTagInput): Tag | null {
    const existing = this.tagById(id);
    if (!existing) return null;
    const objectType = this.objectTypeForCategory(input.categoryId);
    const updated: Tag = {
      ...existing,
      name: input.name.trim(),
      description: input.description.trim(),
      categoryId: input.categoryId,
      color: input.color,
      visibility: input.visibility,
    };
    this._tags.update((tags) => tags.map((tag) => (tag.id === id ? updated : tag)));
    this.replaceAssignments(id, objectType, input.objectIds);
    this.bump();
    return updated;
  }

  deleteTag(id: string): void {
    this._tags.update((tags) => tags.filter((tag) => tag.id !== id));
    this._assignments.update((all) => all.filter((a) => a.tagId !== id));
    this.bump();
  }

  list(query: TagQuery): Observable<TagPage> {
    let result = this.visibleTags().filter(
      (tag) => this.category(tag.categoryId)?.typeKey === 'account',
    );

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter((tag) => tag.name.toLowerCase().includes(term));
    }

    result = this.applyInFilter(result, query.filters['type'], (tag) => {
      const category = this.category(tag.categoryId);
      return category ? category.typeKey : '';
    });
    result = this.applyInFilter(result, query.filters['category'], (tag) => tag.categoryId);
    result = this.applyInFilter(result, query.filters['visibility'], (tag) => tag.visibility);
    result = this.applyInFilter(result, query.filters['color'], (tag) => tag.color);

    const sortField = query.sortField;
    if (sortField) {
      const accessor = this.sortAccessor(sortField);
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => this.compare(accessor(a), accessor(b)) * direction);
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const data = result.slice(start, start + Math.max(1, query.rows));

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  private sortAccessor(field: string): (tag: Tag) => TagSortValue {
    switch (field) {
      case 'category':
        return (tag) => this.category(tag.categoryId)?.name.toLowerCase() ?? '';
      case 'type':
        return (tag) => this.typeOfTag(tag)?.label.toLowerCase() ?? '';
      case 'visibility':
        return (tag) => tag.visibility;
      case 'objectCount':
        return (tag) => this.assignmentCount(tag.id);
      case 'createdAt':
        return (tag) => tag.createdAt;
      default:
        return (tag) => tag.name.toLowerCase();
    }
  }

  private compare(a: TagSortValue, b: TagSortValue): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  private applyInFilter(
    rows: ReadonlyArray<Tag>,
    values: ReadonlyArray<string> | undefined,
    accessor: (tag: Tag) => string,
  ): Tag[] {
    if (!values?.length) return rows.slice();
    const allowed = new Set(values);
    return rows.filter((tag) => allowed.has(accessor(tag)));
  }

  private objectTypeForCategory(categoryId: string): TagTypeKey {
    return this.category(categoryId)?.typeKey ?? 'account';
  }

  private replaceAssignments(
    tagId: string,
    objectType: TagTypeKey,
    objectIds: ReadonlyArray<string>,
  ): void {
    const next: TagAssignment[] = objectIds.map((objectId) => ({ tagId, objectType, objectId }));
    this._assignments.update((all) => [
      ...all.filter((a) => !(a.tagId === tagId && a.objectType === objectType)),
      ...next,
    ]);
  }

  private bump(): void {
    this._revision.update((n) => n + 1);
  }

  private nextId(prefix: string): string {
    const rand = Math.random().toString(36).slice(2, 8);
    return `${prefix}-${Date.now().toString(36)}-${rand}`;
  }

  private todayIso(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
